import { useEffect, useState } from "react";
import {
  LuChevronDown,
  LuChevronLeft,
  LuCreditCard,
  LuMessageCircle,
  LuUser,
} from "react-icons/lu";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import {
  useArePersonalSettingsOpen,
  useInitialPersonalSettingsSection,
  useSideMenuActions,
} from "../../view_stores/useSideMenuStore";
import PersonalSettingsAppearance from "./components/PersonalSettingsAppearance";
import PersonalSettingsContact from "./components/PersonalSettingsContact";
import PersonalSettingsPlans from "./components/PersonalSettingsPlans";
import PersonalSettingsYourProfile from "./components/PersonalSettingsYourProfile";

enum SettingsView {
  LIST = "list",
  PROFILE = "profile",
  APPEARANCE = "appearance",
  PLANS = "plans",
  CONTACT = "contact",
}

export default function PersonalSettings() {
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();
  const initialSection = useInitialPersonalSettingsSection();
  const { closePersonalSettings } = useSideMenuActions();
  const isCompactView = useIsCompactView();
  const [currentView, setCurrentView] = useState<SettingsView>(
    SettingsView.LIST
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isScreenVisible, setIsScreenVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle screen fade in/out animations for compact mode
  useEffect(() => {
    if (isCompactView) {
      if (arePersonalSettingsOpen) {
        // Show the screen immediately when opening
        setShouldRender(true);
        // Small delay to ensure smooth transition
        const timer = setTimeout(() => {
          setIsScreenVisible(true);
        }, 50);
        return () => clearTimeout(timer);
      } else {
        // Start fade out animation
        setIsScreenVisible(false);
        // Wait for fade out to complete before hiding the screen
        const timer = setTimeout(() => {
          setShouldRender(false);
        }, 300); // Match the transition duration
        return () => clearTimeout(timer);
      }
    } else {
      // In default mode, render immediately based on menu state
      setShouldRender(arePersonalSettingsOpen);
      setIsScreenVisible(arePersonalSettingsOpen);
    }
  }, [arePersonalSettingsOpen, isCompactView]);

  // Handle initial section when opening
  useEffect(() => {
    if (arePersonalSettingsOpen && initialSection) {
      // Map section names to SettingsView enum
      const sectionMap: Record<string, SettingsView> = {
        plans: SettingsView.PLANS,
        profile: SettingsView.PROFILE,
        appearance: SettingsView.APPEARANCE,
        contact: SettingsView.CONTACT,
      };

      const targetView = sectionMap[initialSection];
      if (targetView) {
        if (isCompactView) {
          // In compact view, navigate directly to the section
          setCurrentView(targetView);
        } else {
          // In default view, expand the Plans section in the list
          setCurrentView(SettingsView.LIST);
          // We'll handle the expansion in the SettingsItem component
        }
      }
    }
  }, [arePersonalSettingsOpen, initialSection, isCompactView]);

  const handleBack = () => {
    if (currentView !== SettingsView.LIST) {
      if (isCompactView) {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentView(SettingsView.LIST);
          setTimeout(() => {
            setIsTransitioning(false);
          }, 50);
        }, 150);
      } else {
        setCurrentView(SettingsView.LIST);
      }
    } else {
      closePersonalSettings();
    }
  };

  const handleViewChange = (newView: SettingsView) => {
    if (isCompactView) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentView(newView);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50); // Small delay to ensure the new content is rendered
      }, 150); // Fade out duration
    } else {
      setCurrentView(newView);
    }
  };

  const renderFullScreenContent = () => {
    switch (currentView) {
      case "profile":
        return <PersonalSettingsYourProfile />;
      case "appearance":
        return <PersonalSettingsAppearance />;
      case "plans":
        return <PersonalSettingsPlans />;
      case "contact":
        return <PersonalSettingsContact />;
      default:
        return null;
    }
  };

  return (
    <div
      className={`${
        shouldRender
          ? isCompactView
            ? "translate-x-0"
            : "translate-x-[64%]"
          : "-translate-x-[110%]"
      } ${
        isCompactView
          ? "w-full z-50"
          : "w-[500px] z-30 transform transition-transform duration-300 ease-in-out"
      } 
      h-full flex flex-col py-4 fixed inset-y-0
        bg-light-surface-secondary dark:bg-dark-surface-secondary 
        shadow-[5px_0_6px_0_rgba(0,0,0,0.25)] overflow-y-auto
        ${isCompactView ? "transition-opacity duration-300 ease-in-out" : ""}
        ${isCompactView && !isScreenVisible ? "opacity-0" : "opacity-100"}`}
    >
      <div className="px-4">
        <LuChevronLeft
          size={34}
          className="cursor-pointer text-light-text dark:text-dark-text"
          onClick={handleBack}
        />
      </div>

      {currentView === "list" ? (
        <div
          className="flex flex-col gap-4 mt-4 px-4
        text-light-text dark:text-dark-text"
        >
          <SettingsItem
            icon={<LuUser size={24} />}
            label="Your Profile"
            onCompactClick={() => handleViewChange(SettingsView.PROFILE)}
          >
            <PersonalSettingsYourProfile />
          </SettingsItem>
          {/* <SettingsItem
            icon={<LuPalette size={24} />}
            label="Appearance"
            onCompactClick={() => handleViewChange(SettingsView.APPEARANCE)}
          >
            <PersonalSettingsAppearance />
          </SettingsItem> */}
          <SettingsItem
            icon={<LuCreditCard size={24} />}
            label="Plans"
            sectionKey="plans"
            initialSection={initialSection}
            onCompactClick={() => handleViewChange(SettingsView.PLANS)}
          >
            <PersonalSettingsPlans />
          </SettingsItem>
          <SettingsItem
            icon={<LuMessageCircle size={24} />}
            label="Contact Us"
            shouldShowDivider={false}
            onCompactClick={() => handleViewChange(SettingsView.CONTACT)}
          >
            <PersonalSettingsContact />
          </SettingsItem>
        </div>
      ) : (
        <div
          className="flex-1 flex justify-center
        overflow-y-auto p-6"
        >
          <div
            className={`w-full max-w-[500px] transition-opacity duration-300 ease-in-out ${
              isCompactView && isTransitioning ? "opacity-0" : "opacity-100"
            }`}
          >
            {renderFullScreenContent()}
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsItem({
  icon,
  label,
  children,
  shouldShowDivider = true,
  onCompactClick,
  sectionKey,
  initialSection,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  shouldShowDivider?: boolean;
  onCompactClick?: () => void;
  sectionKey?: string;
  initialSection?: string | null;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isCompactView = useIsCompactView();
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();

  // Collapse when menu closes
  useEffect(() => {
    if (!arePersonalSettingsOpen) {
      setIsExpanded(false);
    }
  }, [arePersonalSettingsOpen]);

  // Handle initial expansion when opening with a specific section
  useEffect(() => {
    if (
      arePersonalSettingsOpen &&
      initialSection &&
      sectionKey &&
      initialSection === sectionKey
    ) {
      setIsExpanded(true);
    }
  }, [arePersonalSettingsOpen, initialSection, sectionKey]);

  const handleClick = () => {
    if (isCompactView) {
      onCompactClick?.();
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className="overflow-hidden flex flex-col gap-2">
      <div
        className="flex items-center justify-between p-2 cursor-pointer rounded-lg
        hover:bg-light-hover dark:hover:bg-dark-hover
        transition-all duration-300 ease-in-out"
        onClick={handleClick}
      >
        <div className="flex items-center gap-4">
          <div className="text-light-text-secondary dark:text-dark-text-secondary">
            {icon}
          </div>
          <span className="text-light-text dark:text-dark-text font-medium text-lg">
            {label}
          </span>
        </div>
        {!isCompactView && (
          <LuChevronDown
            size={24}
            className={`text-light-text-secondary dark:text-dark-text-secondary transition-transform duration-300 ease-in-out ${
              isExpanded ? "-rotate-180" : "rotate-0"
            }`}
          />
        )}
      </div>
      {children && !isCompactView && (
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${
            isExpanded
              ? "max-h-[1000px] opacity-100 mt-2"
              : "max-h-0 opacity-0 mt-0"
          }`}
        >
          <div
            className="p-6 rounded-[6px]
          bg-[#C6C9D952] dark:bg-[#2A2E34]
          transform transition-transform duration-300 ease-in-out
          ${isExpanded ? 'translate-y-0' : '-translate-y-2'}"
          >
            {children}
          </div>
        </div>
      )}
      {shouldShowDivider && <Divider isHidden={isExpanded} />}
    </div>
  );
}

function Divider({ isHidden }: { isHidden: boolean }) {
  const isCompactView = useIsCompactView();

  return (
    <hr
      className={`${isHidden ? "opacity-0" : "opacity-100"} 
      ${isCompactView ? "mr-2" : "mr-8"} ml-2
      h-px transition-opacity duration-500
        border-[#8B8B8B4D] dark:border-[#8B8B8B4D]`}
    />
  );
}
